import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import {
    AppNotificationService,
    AppNotificationType,
    assignModelOptions,
    ConfigConstants,
    ConfirmationUploadToDocboxModel,
    isDefined,
    ModalClass,
    Notification,
    NotificationModel,
} from '@evorait/pace-core';
import { DocumentState, initialDocumentState, UploadModalComponent, DocumentStateUpload } from '@evorait/pace-forms';
import { TranslateService } from '@ngx-translate/core';
import { BsModalService } from 'ngx-bootstrap/modal';
import { take } from 'rxjs/operators';

@Component({
    selector: 'pace-confirmation-upload-to-docbox',
    templateUrl: './confirmation-upload-to-docbox.component.html',
    styleUrls: ['./confirmation-upload-to-docbox.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class ConfirmationUploadToDocboxComponent implements OnInit {
    @Input()
    public model: ConfirmationUploadToDocboxModel;

    @Input()
    public parentForm: FormArray;

    @Input()
    public notification: NotificationModel;

    public uploadToDocBoxForm: FormGroup;
    public uploadToDocBoxFormChildren: FormArray;
    private uploadToDocBoxInitialState: {
        docBoxModel: DocumentState;
        notification: Notification;
    };
    public constructor(
        private readonly changeDetector: ChangeDetectorRef,
        private readonly formBuilder: FormBuilder,
        private readonly modalService: BsModalService,
        private readonly translate: TranslateService,
        private readonly appNotificationService: AppNotificationService,
    ) {}

    public ngOnInit(): void {
        this.uploadToDocBoxFormChildren = this.formBuilder.array([]);
        this.uploadToDocBoxForm = this.formBuilder.group({
            children: this.uploadToDocBoxFormChildren,
        });
        this.parentForm.push(this.uploadToDocBoxForm);

        this.prepareData();
        if (this.parentForm) {
            this.parentForm.push(this.uploadToDocBoxForm);
        }
    }

    private prepareData(): void {
        const docBoxModel: DocumentState = {
            ...initialDocumentState,
            orderNumber: this.notification.identifier.qmNum,
            isFileVisible: false,
        };
        const notification = this.notification.getNotificationState();
        this.uploadToDocBoxInitialState = {
            docBoxModel: docBoxModel,
            notification: notification,
        };
    }

    private processResult(document: DocumentStateUpload | undefined): void {
        if (isDefined(document) && document.title.length > 0) {
            this.model.documentTitle.push(document.title);
            this.model.uploadToDocBoxOptions = assignModelOptions(
                this.model.uploadToDocBoxOptions,
                {
                    ...this.model.uploadToDocBoxOptions.options,
                    readonly: true,
                },
                this.model,
            );
        } else if (this.model.documentTitle.length === 0) {
            const initialSelection = 2;
            this.model.uploadToDocBoxOptions.selectByIndex(initialSelection);
        }
        this.uploadToDocBoxFormChildren.controls.forEach((control) => control.updateValueAndValidity());
        this.changeDetector.markForCheck();
    }

    private showWarningForMandatoryUpload(): void {
        this.appNotificationService.setAppNotification(
            AppNotificationType.WARN,
            <string>this.translate.instant('CONFIRMATION_UPLOAD_TO_DOCBOX_MANDATORY_WARNING_TITLE'),
            <string>this.translate.instant('CONFIRMATION_UPLOAD_TO_DOCBOX_MANDATORY_WARNING_BODY'),
            () => {
                this.model.uploadToDocBoxOptions.selectByPredicate(
                    (entry) => entry.value === ConfigConstants.optionValueYes,
                );
                this.onOptionChange();
                this.changeDetector.markForCheck();
            },
        );
    }

    public uploadFile(): void {
        const modalInstance = this.modalService.show(UploadModalComponent, {
            backdrop: 'static',
            class: ModalClass.MODAL_MEDIUM,
            initialState: this.uploadToDocBoxInitialState,
            ariaLabelledBy: 'upload-modal-title',
        });
        modalInstance.content?.modalResult.pipe(take(1)).subscribe((document) => this.processResult(document));
    }

    public onOptionChange(): void {
        // If yes is selected, open the upload to DocBox popup
        if (this.model.uploadToDocBoxOptions.selectedEntry.value === ConfigConstants.optionValueYes) {
            this.uploadFile();
        }
        // If No is selected, and it is mandatory to upload, then display this information to user, and then open upload popup
        if (
            this.model.uploadToDocBoxOptions.selectedEntry.value === ConfigConstants.optionValueNo &&
            this.model.isMandatory
        ) {
            this.showWarningForMandatoryUpload();
        }
    }
}
